import {
  Alert,
  Box,
  Button,
  Container,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import {
  normalizeSignedControlRecord,
  resolveSignedRecordArtifactReportId,
  resolveSignedRecordLineageLabels,
  resolveSignedRecordPageTitle,
} from '../domain/signedControlRecord/recordDetailAdapter';
import { CanonicalRecordHero } from './signedControlRecord/CanonicalRecordHero';
import { SafetyManagementLineageBreadcrumbs } from '../components/safetyReports/SafetyManagementLineageBreadcrumbs';
import { DecisionBand } from './signedControlRecord/DecisionBand';
import { EvidenceStrip } from './signedControlRecord/EvidenceStrip';
import { HotspotTimeConcentration } from './signedControlRecord/HotspotTimeConcentration';
import { LifecycleStory } from './signedControlRecord/LifecycleStory';
import { NativeDetailedRecord } from './signedControlRecord/NativeDetailedRecord';
import { reportsApi, SignedSafetyControlRecord } from '../services/reportsApi';
import { buildLiveSafetyCasePath } from '../utils/acceptanceHierarchyNavigation';
import { buildScanVerifyPath } from '../domain/acceptance/scanVerifyNavigation';
import { buildSafetyManagementWorkspaceHref } from '../domain/safetyCases/safetyCaseDetailTabs';

const LOAD_ERROR = 'This signed record could not be opened. Check your access or return to Signed Records.';

function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
}

function SignedRecordLoading() {
  return (
    <Container
      component="main"
      maxWidth="lg"
      sx={{ py: { xs: 2, md: 4 } }}
      aria-label="Loading signed safety control record"
    >
      <Box
        role="status"
        aria-label="Loading signed safety control record"
        aria-live="polite"
        aria-busy="true"
      >
        <Typography variant="overline" color="text.secondary">
          Signed safety control record
        </Typography>
        <Typography variant="h4" component="h1" sx={{ mt: 0.5, mb: 1 }}>
          Loading signed record
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Opening the exact signed revision and its native report.
        </Typography>
        <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 4 }, borderRadius: 3 }}>
          <Stack spacing={2}>
            <Skeleton variant="text" width="28%" height={24} />
            <Skeleton variant="text" width="72%" height={48} />
            <Skeleton variant="rounded" height={420} />
          </Stack>
        </Paper>
      </Box>
    </Container>
  );
}

export default function SignedSafetyControlRecordPage() {
  const { recordId = '', revision: pathRevision } = useParams();
  const [searchParams] = useSearchParams();
  const revision = useMemo(() => {
    const value = Number(pathRevision || searchParams.get('revision') || searchParams.get('rev'));
    return Number.isInteger(value) && value > 0 ? value : null;
  }, [pathRevision, searchParams]);
  const [record, setRecord] = useState<SignedSafetyControlRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pdfError, setPdfError] = useState('');
  const [scanHtml, setScanHtml] = useState('');

  useEffect(() => {
    let active = true;
    // Always clear prior revision content before a new request so stale signed
    // bodies never remain visible under a later error/404.
    setRecord(null);
    setScanHtml('');
    setPdfError('');
    if (!recordId || revision == null) {
      setLoading(false);
      setError('An exact signed record revision is required.');
      return () => { active = false; };
    }
    setLoading(true);
    setError('');
    const scope = {
      orgId: searchParams.get('org_id'),
      siteId: searchParams.get('site_id') || searchParams.get('site'),
    };
    reportsApi.getSignedSafetyControlRecord(recordId, revision, scope).then((recordResponse) => {
      if (!active) return;
      if (!recordResponse.ok || !recordResponse.record) throw new Error('record unavailable');
      setRecord(recordResponse.record);
      const loadHtml = reportsApi.getCustomerSafetyScanHtml;
      if (typeof loadHtml !== 'function') return;
      void loadHtml(recordId, {
        rev: revision,
        orgId: scope.orgId,
        siteId: scope.siteId,
      }).then((html) => {
        if (!active) return;
        if (
          html
          && (html.includes('canonical-safety-scan') || html.includes('customer-scan-snapshot'))
        ) {
          setScanHtml(html);
        }
      }).catch(() => {
        if (!active) return;
        setScanHtml('');
      });
    }).catch(() => {
      if (!active) return;
      setRecord(null);
      setError(LOAD_ERROR);
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [recordId, revision, searchParams]);

  const sections = useMemo(
    () => (record ? normalizeSignedControlRecord(record) : []),
    [record],
  );
  const pageTitle = useMemo(
    () => (record ? resolveSignedRecordPageTitle(record) : 'Signed Safety Control Record'),
    [record],
  );
  const lineage = useMemo(
    () => (record ? resolveSignedRecordLineageLabels(record) : null),
    [record],
  );
  const workspaceHref = buildSafetyManagementWorkspaceHref(searchParams, { tab: 'records' });
  const artifactParams = new URLSearchParams(searchParams);
  artifactParams.delete('revision');
  artifactParams.delete('rev');
  const artifactReportId = resolveSignedRecordArtifactReportId(record, recordId);
  const artifactHref = `/acceptance/reports/artifacts/${encodeURIComponent(artifactReportId)}${
    artifactParams.toString() ? `?${artifactParams.toString()}` : ''
  }`;
  const viewedCurrentRevision = record != null
    && record.lifecycle !== 'superseded'
    && (
      record.history?.current_revision == null
      || record.history.current_revision === record.revision
    );
  const governedCurrentRevision = record?.signed_body.identity.lifecycle_state === 'superseded'
    && record.history?.current_revision != null
    && record.history.current_revision > record.revision
    ? record.history.current_revision
    : null;
  const currentRevisionHref = governedCurrentRevision == null || record == null
    ? null
    : `/acceptance/reports/records/${encodeURIComponent(record.record_id)}/revisions/${
      governedCurrentRevision
    }${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
  const siteId = searchParams.get('site_id') || searchParams.get('site')
    || (typeof record?.signed_body.scope.site === 'string' ? record.signed_body.scope.site : null);
  const orgId = searchParams.get('org_id');
  const liveCaseHref = record?.related?.safety_case_id
    ? buildLiveSafetyCasePath({
      caseId: record.related.safety_case_id,
      orgId,
      siteId,
      workflowId: record.related.workflow_id,
      incidentId: record.new_activity?.newer_representative_incident_id || undefined,
    })
    : null;
  const sourceScanHref = artifactReportId
    ? buildScanVerifyPath({
      reportId: artifactReportId,
      orgId,
      siteId,
      safetyCaseId: record?.related?.safety_case_id || null,
    })
    : '';

  const downloadPdf = async () => {
    if (!record?.exports?.pdf_available || !record.exports.pdf_mode) return;
    try {
      setPdfError('');
      const { blob, filename } = await reportsApi.downloadSignedSafetyControlRecordPdf(
        record.record_id,
        record.revision,
        { orgId, siteId },
      );
      downloadBlob(blob, filename);
    } catch {
      setPdfError('The PDF export could not be downloaded for this signed revision.');
    }
  };

  if (loading) {
    return <SignedRecordLoading />;
  }

  return (
    <Container component="main" maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
      {error ? (
        <Alert severity="error" role="alert" aria-live="assertive">
          {error}
        </Alert>
      ) : null}
      {record ? (
        <Stack
          component="article"
          aria-labelledby="signed-record-title"
          aria-label={pageTitle}
          spacing={{ xs: 2.5, md: 3.5 }}
          sx={{ minWidth: 0 }}
        >
          <SafetyManagementLineageBreadcrumbs
            ariaLabel="Signed Record parentage"
            testId="signed-record-breadcrumbs"
            crumbs={[
              { label: 'Safety Management', href: workspaceHref },
              { label: lineage?.caseLabel || 'Safety Case', href: liveCaseHref },
              { label: lineage?.scanLabel || 'Safety Scan', href: sourceScanHref || undefined },
              { label: lineage?.recordLabel || 'Signed Record' },
            ]}
          />

          <CanonicalRecordHero
            record={record}
            title={pageTitle}
            actions={(
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {liveCaseHref ? (
                  <Button
                    component={RouterLink}
                    to={liveCaseHref}
                    variant="outlined"
                    sx={{
                      color: '#f8fbff',
                      borderColor: 'rgba(248,251,255,0.55)',
                      '&:hover': { borderColor: '#fff' },
                    }}
                  >
                    Back to Safety Case
                  </Button>
                ) : null}
                {sourceScanHref ? (
                  <Button
                    component={RouterLink}
                    to={sourceScanHref}
                    variant="outlined"
                    data-testid="signed-record-open-source-scan"
                    sx={{
                      color: '#f8fbff',
                      borderColor: 'rgba(248,251,255,0.55)',
                      '&:hover': { borderColor: '#fff' },
                    }}
                  >
                    Open source Safety Scan
                  </Button>
                ) : null}
              </Stack>
            )}
          />

          {record.signed_body?.scope?.observation_window_label || record.signed_body?.finding ? (
            <Alert severity="info" data-testid="signed-record-cm-reference">
              Measurement window: {String(
                record.signed_body?.scope?.observation_window_label
                || 'this signed Scan window',
              )}. Camera measurement context is available on the Safety Scan and is not part of this signed result.
            </Alert>
          ) : (
            <Alert severity="info" data-testid="signed-record-cm-reference">
              Camera measurement context is available on the Safety Scan and is not part of this signed result.
            </Alert>
          )}

          {record.signed_body.identity.lifecycle_state === 'superseded' ? (
            <Alert severity="info" data-testid="superseded-revision-notice">
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                justifyContent="space-between"
                alignItems={{ sm: 'center' }}
              >
                <Typography>
                  Superseded signed revision. This page preserves the exact revision {record.revision}{' '}
                  snapshot.
                </Typography>
                {currentRevisionHref ? (
                  <Button component={RouterLink} to={currentRevisionHref} variant="outlined">
                    View current revision {governedCurrentRevision}
                  </Button>
                ) : null}
              </Stack>
            </Alert>
          ) : null}

          {record.new_activity?.has_new_activity_since_report ? (
            <Alert severity="warning" data-testid="post-sign-new-activity">
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                justifyContent="space-between"
                alignItems={{ sm: 'center' }}
              >
                <Typography>
                  New activity exists after this signed record. It is not part of revision{' '}
                  {record.revision}.
                </Typography>
                {liveCaseHref ? (
                  <Button component={RouterLink} to={liveCaseHref} color="warning" variant="outlined">
                    Review new activity
                  </Button>
                ) : null}
              </Stack>
            </Alert>
          ) : null}

          <Stack spacing={{ xs: 3, md: 5 }} data-testid="immutable-signed-record-body">
            {scanHtml ? (
              <Box
                component="iframe"
                title="Safety Control Scan record"
                srcDoc={scanHtml}
                data-testid="customer-safety-scan-html"
                sx={{
                  width: '100%',
                  minHeight: { xs: 720, md: 1100 },
                  border: '1px solid rgba(8,27,51,0.18)',
                  borderRadius: 2,
                  bgcolor: '#e8eef5',
                }}
              />
            ) : (
              <>
            <LifecycleStory record={record} />
            <HotspotTimeConcentration record={record} />
            <EvidenceStrip record={record} orgId={orgId} siteId={siteId} />
            <DecisionBand record={record} />
            <NativeDetailedRecord
              sections={sections}
              record={record}
              orgId={orgId}
              siteId={siteId}
            />
              </>
            )}
          </Stack>

          <Box
            component="footer"
            sx={{
              p: { xs: 2, md: 2.5 },
              bgcolor: '#f4f7f9',
              borderTop: '1px solid rgba(8,27,51,0.18)',
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              justifyContent="space-between"
              alignItems={{ sm: 'center' }}
            >
              <Box>
                <Typography variant="subtitle2">Exports and supporting artifact</Typography>
                <Typography variant="caption" color="text.secondary">
                  Secondary export · PDF export is not approval.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {record.exports?.pdf_available && record.exports.pdf_mode ? (
                  <Button variant="outlined" onClick={() => void downloadPdf()}>
                    Download Signed Record PDF
                  </Button>
                ) : null}
                {viewedCurrentRevision ? (
                  <Button component={RouterLink} to={artifactHref} variant="text">
                    View artifact details
                  </Button>
                ) : null}
              </Stack>
            </Stack>
            {pdfError ? (
              <Alert severity="error" role="alert" sx={{ mt: 1.5 }}>{pdfError}</Alert>
            ) : null}
          </Box>
        </Stack>
      ) : null}
    </Container>
  );
}
